import React from 'react';
import { ArrowRight, Server, Shield, Headphones } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';

export const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="accueil" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.pexels.com/photos/30820142/pexels-photo-30820142.jpeg" 
          alt="Tech Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        {/* Network Lines Effect */}
        <div className="absolute inset-0 network-lines opacity-20"></div>
      </div>

      {/* Glowing Orange Accent */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#FF6B35] opacity-10 blur-[150px] rounded-full"></div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-6">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] px-4 py-2 border border-[#FF6B35]/30 rounded-full bg-[#FF6B35]/10">
                Services Technologiques Professionnels
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-6 tracking-tighter leading-tight">
              L'Innovation
              <span className="block text-[#FF6B35]">Technologique</span>
              <span className="block">Pour Votre PME</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl">
              Infrastructure TI, téléphonie VoIP, solutions de paiement et logiciels de gestion. 
              <span className="text-white font-semibold"> Support technique 24/7</span> pour propulser votre entreprise.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#FF6B35] text-white font-semibold rounded-md px-8 py-6 text-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] group"
              >
                Obtenir une soumission gratuite
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => scrollToSection('services')}
                variant="outline"
                className="bg-transparent border-2 border-white/20 text-white font-semibold rounded-md px-8 py-6 text-lg hover:border-white transition-all"
              >
                Découvrir nos services
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center lg:text-left"
              >
                <div className="text-3xl md:text-4xl font-black text-[#FF6B35] mb-1">24/7</div>
                <div className="text-xs md:text-sm text-gray-400 uppercase tracking-wide">Support Technique</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-center lg:text-left"
              >
                <div className="text-3xl md:text-4xl font-black text-[#FF6B35] mb-1">200+</div>
                <div className="text-xs md:text-sm text-gray-400 uppercase tracking-wide">Clients PME</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center lg:text-left"
              >
                <div className="text-3xl md:text-4xl font-black text-[#FF6B35] mb-1">15+</div>
                <div className="text-xs md:text-sm text-gray-400 uppercase tracking-wide">Ans d'expertise</div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Feature Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:grid grid-cols-2 gap-4"
          >
            <div className="bg-[#121212]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all hover:-translate-y-1 group">
              <div className="w-12 h-12 bg-[#FF6B35]/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#FF6B35]/30 transition-colors">
                <Server className="w-6 h-6 text-[#FF6B35]" />
              </div>
              <h3 className="text-lg font-bold mb-2">Infrastructure TI</h3>
              <p className="text-sm text-gray-400">Réseaux, serveurs et cloud sécurisés</p>
            </div>

            <div className="bg-[#121212]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all hover:-translate-y-1 group mt-8">
              <div className="w-12 h-12 bg-[#FF6B35]/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#FF6B35]/30 transition-colors">
                <Shield className="w-6 h-6 text-[#FF6B35]" />
              </div>
              <h3 className="text-lg font-bold mb-2">Cybersécurité</h3>
              <p className="text-sm text-gray-400">Protection avancée de vos données</p>
            </div>

            <div className="bg-[#121212]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all hover:-translate-y-1 group -mt-8">
              <div className="w-12 h-12 bg-[#FF6B35]/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#FF6B35]/30 transition-colors">
                <Headphones className="w-6 h-6 text-[#FF6B35]" />
              </div>
              <h3 className="text-lg font-bold mb-2">Support 24/7</h3>
              <p className="text-sm text-gray-400">Assistance technique rapide</p>
            </div>

            <div className="bg-gradient-to-br from-[#FF6B35] to-[#FF8555] rounded-xl p-6 hover:shadow-[0_0_30px_rgba(255,107,53,0.5)] transition-all hover:-translate-y-1 cursor-pointer">
              <h3 className="text-lg font-bold mb-2">+10 Services</h3>
              <p className="text-sm mb-4">Solutions IT complètes pour PME</p>
              <div className="flex items-center text-sm font-semibold">
                Voir tous les services
                <ArrowRight className="ml-2 w-4 h-4" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <div className="flex flex-col items-center gap-2 text-gray-400">
          <span className="text-xs uppercase tracking-wider">Scroll</span>
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex items-start justify-center p-1">
            <motion.div 
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};
